import { useTranslate } from 'react-admin';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useResponsive } from 'src/hooks/use-responsive';

import { PAUSE_REASON_MAX, VAULTS_NS as NS } from './constants';
import type { VaultOperationsForm } from './useVaultOperations';

const CONFIRM = {
  pause: 'confirmPause',
  takeOver: 'confirmTakeOver',
  resume: 'confirmResume',
} as const;
const TITLE = { pause: 'pauseTitle', takeOver: 'takeOverTitle', resume: 'resumeTitle' } as const;
const BODY = { pause: 'pauseBody', takeOver: 'takeOverBody', resume: 'resumeBody' } as const;

/**
 * XBIT-4242: pause a vault's subscriptions, take over the manager's pause, or resume an operations pause. Pausing needs
 * a reason, which the engine keeps on its audit row; resuming needs none. Redemptions are never touched.
 */
export const OperationsDialog = ({
  form,
  vaultName,
}: {
  form: VaultOperationsForm;
  vaultName: string;
}) => {
  const translate = useTranslate();
  const smDown = useResponsive('down', 'sm');
  const t = (key: string, options?: object) =>
    translate(`${NS}.detail.operations.dialog.${key}`, options);
  const { mode } = form;
  const count = Array.from(form.reason.trim()).length;

  return (
    <Dialog
      open={mode !== null}
      onClose={form.submitting ? undefined : form.close}
      maxWidth="sm"
      fullWidth
      fullScreen={smDown}
    >
      {mode && (
        <>
          <DialogTitle>{t(TITLE[mode], { name: vaultName })}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2">{t(BODY[mode])}</Typography>
              {form.alert && (
                <Alert severity={form.alert.severity}>
                  <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
                    {form.alert.messages.map((message) => (
                      <li key={message}>{message}</li>
                    ))}
                  </Stack>
                </Alert>
              )}
              {mode !== 'resume' && (
                <TextField
                  required
                  fullWidth
                  multiline
                  minRows={3}
                  label={t('reason')}
                  value={form.reason}
                  error={Boolean(form.error)}
                  helperText={[
                    form.error && translate(form.error, { max: PAUSE_REASON_MAX }),
                    t('reasonHint', { count, max: PAUSE_REASON_MAX }),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  onChange={(e) => form.setReason(e.target.value)}
                />
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={form.close} disabled={form.submitting}>
              {t('cancel')}
            </Button>
            <LoadingButton
              variant="contained"
              color={mode === 'resume' ? 'primary' : 'warning'}
              onClick={form.submit}
              disabled={form.submitting}
              loading={form.submitting}
            >
              {form.alert ? t('retry') : t(CONFIRM[mode])}
            </LoadingButton>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
